import {
    AvailabilityStatus,
    BookingStatus,
    ClinicianCredential,
    DeleteActions,
    IAvailabilitySchedule,
    IClient,
    IClientBooking,
    IClinician,
    IExperienceLevelOption,
    IIntakeAvailability,
    IIntakeSessionHistory,
    IOfficeSchedule,
    IOfficeWindow,
    IReservedSchedule,
    RecurrenceRule,
    ServiceDeliveryOption,
} from "intake_shared/src/types";
import { AclContext } from "../../../../contexts/AclContext";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";
import styles from "./ClinicianAvailability.module.scss";
import * as React from "react";
import Alert from "../../bits/Alert/Alert";
import store from "../../../../store";
import { uniq } from "lodash";
import { useToasts } from "react-toast-notifications";
import { Button } from "../../bits/Button/Button";
import { BackButton } from "../../bits/BackButton/BackButton";
import { useHistory } from "react-router-dom";
import { Badge } from "../../bits/Badge/Badge";
import { MetaBox } from "../../bits/MetaBox/MetaBox";
import { Tabs } from "../../bits/Tabs/Tabs";
import { Loader } from "../../bits/Loader/Loader";
import Caseload from "../Caseload/Caseload";
import { Input } from "../../bits/Input/Input";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import EditIcon from "../../icons/Edit";
import SaveCheckIcon from "../../icons/SaveCheck";
import CancelIcon from "../../icons/Cancel";
import { InfoIcon } from "../../icons/Info";
import ExperienceLevels from "../ExperienceLevels/ExperienceLevels";
import HistoryWrapper from "../HistoryWrapper/HistoryWrapper";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import ChevronDownIcon from "../../icons/ChevronDown";
import { Switch } from "../../bits/Switch/Switch";
import { CalendarId } from "../../../../constants";
import moment from "../../../../utils/app_moment";
import { v4 as uuidv4 } from "uuid";
import {
    generateRandomColor,
    getHashNumber,
    findAvailableSlotsThatMatchOfficeWindow,
    findLabelByPracticeId,
    getClinicianName,
} from "../../../../utils/utils";
import Calendar from "../../calendar/Calendar/Calendar";
import { ToggleButton } from "../../bits/ToggleButton/ToggleButton";
import CalendarBoxView from "../../calendar/CalendarBoxView/CalendarBoxView";
import AddAvailabilityDialog from "./AddAvailabilityDialog/AddAvailabilityDialog";
import ReserveTimeWindowDialog from "./ReserveTimeWindowDialog/ReserveTimeWindowDialog";
import TimeOffDialog from "./TimeOffDialog/TimeOffDialog";
import AddIntakeSessionDialog from "./AddIntakeSessionDialog/AddIntakeSessionDialog";
import RescheduleIntakeSessionDialog from "./RescheduleIntakeSessionDialog/RescheduleIntakeSessionDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import { TextArea } from "../../bits/TextArea/TextArea";
import { Views } from "react-big-calendar";
import FilterButton from "../../bits/FilterButton/FilterButton";
import ChevronBackIcon from "../../icons/ChevronBack";
import ChevronRightIcon from "../../icons/ChevronRight";
import { OfficeRequestStatus } from "intake_shared/src/types";
import { ClinicianServiceSelection } from "../../../../store/department";
import {
    findAvailabilityConflicts,
    getIntakeSessionConflictWarning,
    getMatchingOfficeWindow,
    scheduleCrossesLocationBoundary,
} from "../../../v2utils";
import { arrayUnion } from "firebase/firestore";
import appSettings from "../../../../appSettings";
import CredentialExpirationStatus from "../CredentialExpirationStatus/CredentialExpirationStatus";

const CalendarColor = {
    OFFICE: generateRandomColor(getHashNumber("1")),
    AVAILABILITY: generateRandomColor(getHashNumber("3")),
    UNAVAILABLITY: generateRandomColor(getHashNumber("5")),
    HELD_BOOKINGS: generateRandomColor(getHashNumber("7")),
    CONFIRMED_BOOKINGS: generateRandomColor(getHashNumber("9")),
    INTAKE_SESSION_AVAILABILITY: generateRandomColor(getHashNumber("11")),
};

enum CalendarName {
    OFFICE = "Office Window",
    AVAILABILITY = "Availability",
    UNAVAILABLITY = "Unavailability",
    HELD_BOOKINGS = "Held",
    CONFIRMED_BOOKINGS = "Confirmed",
    INTAKE_SESSION_AVAILABILITY = "Intake Sessions",
}

interface ClinicianAvailabilityProps {
    loggedInUserEmailProp?: string;
    delegateUserEmail?: string;
    headerActions?: React.ReactNode;
}

interface IntakeSessionDraft {
    from: Date;
    to: Date;
    serviceDeliveryOption: ServiceDeliveryOption;
    practice_id: string;
}

export default function ClinicianAvailability({
    loggedInUserEmailProp,
    delegateUserEmail,
    headerActions,
}: ClinicianAvailabilityProps) {
    const history = useHistory();
    const aclContext = React.useContext(AclContext);
    const olc = React.useContext(CacheContext);
    const allEmailSignatures = olc?.emailSignatures || {};
    const intakeSignature = getIntakeEmailSignature(olc);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const [showLoader, setShowLoader] = React.useState(false);
    const [showIntakeActionLoader, setShowIntakeActionLoader] =
        React.useState(false);
    const [caseloadClients, setCaseloadClients] = React.useState(
        {} as { [email: string]: IClient }
    );
    const [clinicianServiceSelection, setClinicianServiceSelection] =
        React.useState<ClinicianServiceSelection>({});
    const [caseLoadCountByFilter, setCaseLoadCountByFilter] = React.useState(0);
    const loggedInUserEmail =
        loggedInUserEmailProp || aclContext?.authUser.user?.email;
    const { addToast } = useToasts();

    const [clinicianData, setClinicianData] = React.useState({} as IClinician);
    const loggedInClinician = React.useMemo(
        () => ({
            ...clinicianData,
            confirmed: (clinicianData.confirmed ?? []).filter(
                (booking) => caseloadClients[booking.client_id]
            ),
            held: (clinicianData.held ?? []).filter(
                (booking) => caseloadClients[booking.client_id]
            ),
        }),
        [clinicianData, caseloadClients]
    );
    const [allExperienceLevelOptions, setAllExperienceLevelOptions] = React.useState<
        IExperienceLevelOption[]
    >([]);

    const [availabilitySchedules, setAvailabilitySchedules] = React.useState(
        [] as IAvailabilitySchedule[]
    );

    const [unavailabilitySchedules, setUnavailabilitySchedules] = React.useState(
        [] as IAvailabilitySchedule[]
    );

    const [draftAvailability, setDraftAvailability] = React.useState<{
        status: AvailabilityStatus;
        from: Date;
        to: Date;
        recurrenceRule: RecurrenceRule;
        reservedFor: string;
        endDate?: Date;
        availableService?: string;
        serviceDeliveryOption: ServiceDeliveryOption;
    }>({
        status: AvailabilityStatus.AVAILABLE,
        from: new Date(),
        to: moment().add(30, "minutes").toDate(),
        recurrenceRule: RecurrenceRule.WEEKLY,
        reservedFor: "",
        endDate: undefined,
        availableService: "",
        serviceDeliveryOption: "in_person",
    });

    const [calendarSwitch, setCalendarSwitch] = React.useState({
        [CalendarId.OFFICE]: false,
        [CalendarId.AVAILABILITY]: true,
        [CalendarId.UNAVAILABLITY]: true,
        [CalendarId.HELD_BOOKINGS]: true,
        [CalendarId.CONFIRMED_BOOKINGS]: true,
        [CalendarId.INTAKE_SESSION_AVAILABILITY]: true,
    });

    const [activeSwitch, setActiveSwitch] = React.useState("list");

    const [activeTab, setActiveTab] = React.useState("caseload");
    const [showExpLevels, setShowExpLevels] = React.useState(false);
    const [isEditingClientContact, setIsEditingClientContact] =
        React.useState(false);
    const [isEditingPersonalContact, setIsEditingPersonalContact] =
        React.useState(false);
    const [isEditingIdealCaseload, setIsEditingIdealCaseload] =
        React.useState(false);
    const [idealCaseload, setIdealCaseload] = React.useState(
        loggedInClinician.ideal_caseload || ""
    );
    const [clientContact, setClientContact] = React.useState("");
    const [personalContact, setPersonalContact] = React.useState("");
    const [showClientContactPromptDialog, setShowClientContactPromptDialog] =
        React.useState(false);
    // Calendar toolbar state
    const [currentView, setCurrentView] = React.useState(() => {
        const savedView = localStorage.getItem("clinicianAvailability_currentView");
        return savedView || Views.WEEK;
    });
    const [currentDate, setCurrentDate] = React.useState(() => {
        const savedDate = localStorage.getItem("clinicianAvailability_currentDate");
        return savedDate ? new Date(savedDate) : new Date();
    });

    const [showAddAvailabilityDialog, setShowAddAvailabilityDialog] =
        React.useState(false);
    const [showReserveTimeWindowDialog, setShowReserveTimeWindowDialog] =
        React.useState(false);
    const [showTimeOffDialog, setShowTimeOffDialog] = React.useState(false);
    const [showAddIntakeSessionDialog, setShowAddIntakeSessionDialog] =
        React.useState(false);

    const [draftReservedWindow, setDraftReservedWindow] = React.useState({
        status: AvailabilityStatus.AVAILABLE,
        availableService: "",
        reservedFor: "",
        from: new Date(),
        to: new Date(),
        recurrenceRule: RecurrenceRule.WEEKLY,
        serviceDeliveryOption: "in_person" as ServiceDeliveryOption,
        practice_id: "",
    });

    const [draftVacation, setDraftVacation] = React.useState({
        startingDate: new Date(),
        endingDate: new Date(),
        updatedAt: new Date(),
    });

    const [officeSchedulesInReview, setOfficeSchedulesInReview] = React.useState(
        [] as IAvailabilitySchedule[]
    );

    const [refreshKey, setRefreshKey] = React.useState(0);

    const [intakeSessionAvailability, setIntakeSessionAvailability] = React.useState<
        IIntakeAvailability[]
    >([]);

    const [draftIntakeSession, setDraftIntakeSession] =
        React.useState<IntakeSessionDraft>({
            from: new Date(),
            to: moment().add(45, "minutes").toDate(),
            serviceDeliveryOption: "remote" as ServiceDeliveryOption,
            practice_id: "",
        });

    const clinicianIntakePracticeDepartments = new Set(
        loggedInClinician.departments || []
    );
    if (
        loggedInClinician.email &&
        appSettings.SUPERVISORS_WITH_PH_DEPARTMENT.includes(loggedInClinician.email)
    ) {
        clinicianIntakePracticeDepartments.add("PH");
    }

    const intakeSessionPracticeOptions = Array.from(
        clinicianIntakePracticeDepartments
    )
        .map((department) => {
            const departmentData = allDepartmentsAndServices[department];
            return {
                value: departmentData?.practice_id || "",
                label: departmentData?.label || department.replaceAll("_", " "),
            };
        })
        .filter((option) => option.value);

    // State for intake session actions
    const [showCancelIntakeDialog, setShowCancelIntakeDialog] =
        React.useState(false);
    const [showEditIntakeSessionDialog, setShowEditIntakeSessionDialog] =
        React.useState(false);
    const [showEditAvailableIntakeDialog, setShowEditAvailableIntakeDialog] =
        React.useState(false);
    const [selectedIntakeSchedule, setSelectedIntakeSchedule] =
        React.useState<IAvailabilitySchedule | null>(null);
    const [intakeActionReason, setIntakeActionReason] = React.useState("");

    const handleSwitch = (key: string) => {
        setActiveSwitch(key);
    };

    // Calendar toolbar functions
    const goToToday = () => {
        const today = new Date();
        setCurrentDate(today);
        localStorage.setItem(
            "clinicianAvailability_currentDate",
            today.toISOString()
        );
    };

    const goToPrevious = () => {
        const newDate = new Date(currentDate);
        if (currentView === Views.DAY) {
            newDate.setDate(newDate.getDate() - 1);
        } else if (currentView === Views.WEEK) {
            newDate.setDate(newDate.getDate() - 7);
        } else if (currentView === Views.MONTH) {
            newDate.setMonth(newDate.getMonth() - 1);
        }
        setCurrentDate(newDate);
        localStorage.setItem(
            "clinicianAvailability_currentDate",
            newDate.toISOString()
        );
    };

    const goToNext = () => {
        const newDate = new Date(currentDate);
        if (currentView === Views.DAY) {
            newDate.setDate(newDate.getDate() + 1);
        } else if (currentView === Views.WEEK) {
            newDate.setDate(newDate.getDate() + 7);
        } else if (currentView === Views.MONTH) {
            newDate.setMonth(newDate.getMonth() + 1);
        }
        setCurrentDate(newDate);
        localStorage.setItem(
            "clinicianAvailability_currentDate",
            newDate.toISOString()
        );
    };

    const goToView = (newView: string) => {
        setCurrentView(newView as any);
        localStorage.setItem("clinicianAvailability_currentView", newView);
    };

    const getViewLabel = () => {
        switch (currentView) {
            case Views.DAY:
                return "Day";
            case Views.WEEK:
                return "Week";
            case Views.MONTH:
                return "Month";
            default:
                return "Week";
        }
    };

    const formatDateRange = () => {
        const start = moment(currentDate);

        if (currentView === Views.DAY) {
            return start.format("MMM DD, YYYY");
        } else if (currentView === Views.WEEK) {
            const weekStart = start.clone().startOf("week");
            const weekEnd = weekStart.clone().endOf("week");
            return `${weekStart.format("MMM DD")} - ${weekEnd.format(
                "MMM DD, YYYY"
            )}`;
        } else if (currentView === Views.MONTH) {
            return start.format("MMMM YYYY");
        }
        return start.format("MMM DD, YYYY");
    };

    const viewOptions = [
        { value: Views.DAY, label: "Day" },
        { value: Views.WEEK, label: "Week" },
        { value: Views.MONTH, label: "Month" },
    ];

    const getClinician = async (loggedInUserEmail: string) => {
        setShowLoader(true);
        try {
            const clinician = await store.getClinicianByEmail(loggedInUserEmail);
            if (clinician) {
                clinician.held = (clinician.held || []).filter((h) => h.id);
                clinician.confirmed = (clinician.confirmed || []).filter(
                    (c) => c.id
                );
                setClientContact(clinician.contact_info_client || "");
                setPersonalContact(clinician.contact_info_personal || "");
                setIdealCaseload(clinician.ideal_caseload || "");
                setClinicianData(clinician);
                const bookedIds = new Set([
                    ...(clinician.confirmed || []).map((b) => b.id),
                    ...(clinician.held || []).map((b) => b.id),
                ]);
                setAvailabilitySchedules(
                    (clinician.available || []).filter((a) => !bookedIds.has(a.id))
                );
                setUnavailabilitySchedules(clinician.unavailable);
                setIntakeSessionAvailability(
                    (clinician.available_for_intake_sessions || []).filter(
                        (s) =>
                            s.status === "available" && !s.booking_id && !s.client_id
                    )
                );
                setClinicianServiceSelection(
                    await store.fetchClinicianServiceSelection(clinician.id!)
                );
            }
        } finally {
            setShowLoader(false);
        }
    };

    React.useEffect(() => {
        const clinicianLoaded = Boolean(loggedInClinician?.id);
        const missingClientContact = !clientContact?.trim();

        if (clinicianLoaded && missingClientContact) {
            setShowClientContactPromptDialog(true);
        }

        if (!missingClientContact) {
            setShowClientContactPromptDialog(false);
        }
    }, [clientContact, loggedInClinician?.id]);
    const officeWindowSchedules: IOfficeSchedule[] = (
        loggedInClinician.office_windows || []
    ).map((w, idx) => {
        return {
            id: w.id,
            title: `${w.location} ${w.office}`,
            start: w.start,
            end: w.end,
            ending_date: w.ending_date,
            location: w.location,
            office: w.office,
            category: "time",
            isReadOnly: false,
            calendarId: CalendarId.OFFICE,
        };
    });

    // Helper to convert intake session to schedule format for conflict checking
    const intakeToSchedule = (
        intake: IIntakeAvailability,
        tempId?: string
    ): IAvailabilitySchedule => {
        return {
            id: intake.id || tempId,
            title: "Intake Session",
            start: intake.start_time,
            end: intake.end_time,
            category: "time",
            recurrenceRule: RecurrenceRule.ONE_OFF,
            calendarId: CalendarId.INTAKE_SESSION_AVAILABILITY,
        };
    };

    const checkIntakeSessionConflicts = (
        newIntakeSession: IIntakeAvailability,
        excludeId?: string,
        excludeBookingId?: string
    ): boolean => {
        const scheduleForCheck = intakeToSchedule(newIntakeSession, excludeId);

        const intakeSchedulesToCheck = [
            ...availableIntakeSessionSchedules.filter(
                (s) => !excludeId || s.id !== excludeId
            ),
            ...bookedIntakeSessionSchedules.filter(
                (s) => !excludeBookingId || (s as any).bookingId !== excludeBookingId
            ),
        ];

        const warningSchedulesToCheck = [
            ...availabilitySchedules,
            ...heldBookingSchedules,
            ...confirmedBookingSchedules.filter(
                (b) => !excludeBookingId || b.id !== excludeBookingId
            ),
        ];

        const { hasConflicts: hasIntakeConflicts } = getIntakeSessionConflictWarning(
            intakeSchedulesToCheck,
            [],
            [],
            scheduleForCheck
        );

        if (hasIntakeConflicts) {
            addToast(
                "There is already an existing intake session for that day/time. Please choose another time.",
                {
                    appearance: "error",
                    autoDismiss: false,
                }
            );
            return true;
        }

        const { hasConflicts: hasWarningConflicts, message } =
            getIntakeSessionConflictWarning(
                warningSchedulesToCheck,
                [],
                [],
                scheduleForCheck
            );

        if (hasWarningConflicts && message) {
            addToast(message, {
                appearance: "warning",
                autoDismiss: false,
            });
        }
        return false;
    };

    const convertBookingToSchedule = (
        w: IClientBooking,
        calendarId: CalendarId,
        bgColor = "#fff"
    ): IAvailabilitySchedule => {
        // get client detail at this point so we can have access to client firstname or lastname that may have been updated
        const clientDetail = caseloadClients[w.client_id];

        return {
            ...w,
            title: `${clientDetail?.first_name} ${clientDetail?.last_name}`,
            location: w.location,
            office: w.office,
            category: "time",
            isReadOnly: true,
            calendarId,
            bgColor,
        };
    };

    const heldBookingSchedules: IAvailabilitySchedule[] = (
        loggedInClinician.held || []
    ).map((h) =>
        convertBookingToSchedule(
            h,
            CalendarId.HELD_BOOKINGS,
            CalendarColor.HELD_BOOKINGS
        )
    );

    const confirmedBookingSchedules: IAvailabilitySchedule[] = (
        loggedInClinician.confirmed || []
    ).map((c) =>
        convertBookingToSchedule(
            c,
            CalendarId.CONFIRMED_BOOKINGS,
            CalendarColor.CONFIRMED_BOOKINGS
        )
    );

    const getIntakeSessionPracticeTitle = (practiceId?: string) => {
        const practiceLabel = practiceId
            ? findLabelByPracticeId(practiceId, allDepartmentsAndServices)
            : "";
        return practiceLabel ? ` - ${practiceLabel}` : "";
    };

    // Convert available intake sessions to schedules
    const availableIntakeSessionSchedules: IAvailabilitySchedule[] = (
        intakeSessionAvailability || []
    ).map((slot) => {
        const tempSchedule: IAvailabilitySchedule = {
            id: slot.id,
            title: "Intake Session",
            start: slot.start_time,
            end: slot.end_time,
            category: "time",
            recurrenceRule: RecurrenceRule.ONE_OFF,
            calendarId: CalendarId.INTAKE_SESSION_AVAILABILITY,
        };

        const matchingWindow = getMatchingOfficeWindow(
            officeWindowSchedules,
            tempSchedule
        );

        const intakeColor = {
            backgroundColor: "#E53935",
            titleColor: "#FFFFFF",
            borderColor: "#B71C1C",
        };

        return {
            id: slot.id,
            title: `Intake Session${getIntakeSessionPracticeTitle(
                slot.practice_id
            )} (${
                slot.service_delivery_option === "remote" ? "Remote" : "In-Person"
            })`,
            start: slot.start_time,
            end: slot.end_time,
            category: "time",
            isReadOnly: false,
            canEdit: true,
            calendarId: CalendarId.INTAKE_SESSION_AVAILABILITY,
            bgColor: intakeColor.backgroundColor,
            serviceDeliveryOption: slot.service_delivery_option,
            recurrenceRule: RecurrenceRule.ONE_OFF,
            location:
                slot.service_delivery_option === "remote"
                    ? "Remote"
                    : matchingWindow?.location,
            office:
                slot.service_delivery_option === "remote"
                    ? `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`
                    : matchingWindow?.office,
            isIntakeSession: true,
            supervisor_id: loggedInClinician?.id || "",
            clinician_display_name: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
            status: "available",
            practice_id: slot.practice_id || "",
            isIntakeSessionAvailable: true,
        } as IAvailabilitySchedule & {
            isIntakeSession?: boolean;
            isIntakeSessionAvailable?: boolean;
            supervisor_id?: string;
        };
    });

    const bookedIntakeSessionSchedules: IAvailabilitySchedule[] = (
        loggedInClinician.available_for_intake_sessions || []
    )
        .filter((session) => session.booking_id && session.client_id)
        .map((session) => {
            return {
                id: session.id,
                title: `Intake Session${getIntakeSessionPracticeTitle(
                    session.practice_id
                )}: ${session.client_display_name || ""}`,
                start: session.start_time,
                end: session.end_time,
                category: "time",
                isReadOnly: session.status === "held",
                canEdit: session.status === "confirmed",
                calendarId: CalendarId.INTAKE_SESSION_AVAILABILITY,
                bgColor: "#FB8C00",
                serviceDeliveryOption: session.service_delivery_option,
                recurrenceRule: RecurrenceRule.ONE_OFF,
                location: session.location,
                office: session.office,
                isIntakeSession: true,
                isIntakeSessionBooked: true,
                status: session.status,
                supervisor_id: loggedInClinician?.id || "",
                clinician_display_name: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
                bookingId: session.booking_id,
                client_id: session.client_id,
                practice_id: session.practice_id || "",
            } as IAvailabilitySchedule & {
                isIntakeSession?: boolean;
                isIntakeSessionBooked?: boolean;
                bookingId?: string;
                client_id?: string;
                practice_id?: string;
            };
        });

    const manageAvailability = async (
        action: "add" | "remove" | "update",
        schedule: IAvailabilitySchedule | IReservedSchedule,
        status?: AvailabilityStatus
    ) => {
        switch (action) {
            case "add": {
                const saveAvailability = async (
                    schedule: IAvailabilitySchedule,
                    status: AvailabilityStatus
                ) => {
                    const canSetAvailabilityEndDate =
                        loggedInClinician.credential === ClinicianCredential.INTERN;
                    const scheduleToSave = { ...schedule };
                    if (
                        status === AvailabilityStatus.AVAILABLE &&
                        !canSetAvailabilityEndDate
                    ) {
                        delete scheduleToSave.ending_date;
                    }

                    setShowLoader(true);
                    try {
                        await store.addClinicianAvailability(
                            loggedInClinician.id || loggedInClinician.email,
                            status,
                            scheduleToSave
                        );
                        if (status === AvailabilityStatus.AVAILABLE) {
                            setAvailabilitySchedules([
                                ...availabilitySchedules,
                                { ...scheduleToSave },
                            ]);
                        } else {
                            setUnavailabilitySchedules([
                                ...unavailabilitySchedules,
                                { ...scheduleToSave },
                            ]);
                        }
                        setDraftAvailability({
                            ...draftAvailability,
                        });
                    } finally {
                        setShowLoader(false);
                    }
                };

                if (
                    (status || draftAvailability.status) ===
                    AvailabilityStatus.UNAVAILABLE
                ) {
                    setShowLoader(true);
                    await saveAvailability(schedule, AvailabilityStatus.UNAVAILABLE);
                    addToast("Vacation dates saved", { appearance: "success" });
                    setShowLoader(false);
                    return;
                }

                const hasConflictingAvailability = findAvailabilityConflicts(
                    availabilitySchedules,
                    heldBookingSchedules,
                    confirmedBookingSchedules,
                    schedule
                );

                if (hasConflictingAvailability) {
                    addToast(
                        "Duplicate availability window or conflicts with an existing appointment",
                        {
                            appearance: "error",
                        }
                    );
                    return;
                }
                const fallsBetweenOfficeWindow = getMatchingOfficeWindow(
                    officeWindowSchedules,
                    schedule
                );

                if (fallsBetweenOfficeWindow) {
                    const availabilitySchedule = {
                        ...schedule,
                        location: fallsBetweenOfficeWindow.location,
                        office: fallsBetweenOfficeWindow.office,
                    };
                    await saveAvailability(
                        availabilitySchedule,
                        AvailabilityStatus.AVAILABLE
                    );
                    if ((schedule as IReservedSchedule).reservedFor) {
                        try {
                            await store.reserveAppointment(availabilitySchedule);
                        } catch (error) {
                            try {
                                await store.updateClinicianAvailability(
                                    loggedInClinician.id || loggedInClinician.email,
                                    AvailabilityStatus.AVAILABLE,
                                    availabilitySchedules
                                );
                                setAvailabilitySchedules([...availabilitySchedules]);
                            } catch (rollbackError) {
                                console.error(
                                    "Failed to rollback reserved time window availability",
                                    {
                                        message:
                                            rollbackError instanceof Error
                                                ? rollbackError.message
                                                : "Unknown error",
                                        clinicianId:
                                            loggedInClinician.id ||
                                            loggedInClinician.email,
                                        scheduleId: availabilitySchedule.id,
                                    }
                                );
                            }
                            console.error("Failed to reserve time window", {
                                message:
                                    error instanceof Error
                                        ? error.message
                                        : "Unknown error",
                                clinicianId:
                                    loggedInClinician.id || loggedInClinician.email,
                                scheduleId: availabilitySchedule.id,
                            });
                            throw error;
                        }
                    }
                    addToast("Availability saved", { appearance: "success" });
                    setShowAddAvailabilityDialog(false);
                    setShowReserveTimeWindowDialog(false);
                } else {
                    const crossesLocationBoundary = scheduleCrossesLocationBoundary(
                        officeWindowSchedules,
                        schedule
                    );
                    addToast(
                        crossesLocationBoundary
                            ? "Availability window spans two different office locations — please split into separate slots"
                            : "Cannot set availability outside of Office window",
                        { appearance: "error" }
                    );
                    setAvailabilitySchedules([...availabilitySchedules]);
                }
                break;
            }
            case "update": {
                if (schedule.title === "Available") {
                    const { id, end, start, recurrenceRule, ending_date } = schedule;
                    if (!id) {
                        break;
                    }
                    const canSetAvailabilityEndDate =
                        loggedInClinician.credential === ClinicianCredential.INTERN;
                    const hasConflictingAvailability = findAvailabilityConflicts(
                        availabilitySchedules,
                        heldBookingSchedules,
                        confirmedBookingSchedules,
                        schedule
                    );

                    if (hasConflictingAvailability) {
                        addToast(
                            "Duplicate availability window or conflicts with an existing appointment",
                            {
                                appearance: "error",
                            }
                        );
                        return;
                    }

                    const fallsBetweenOfficeWindow = getMatchingOfficeWindow(
                        officeWindowSchedules,
                        schedule
                    );

                    if (fallsBetweenOfficeWindow) {
                        setShowLoader(true);
                        try {
                            await store.updateAvailabilitySchedule(
                                loggedInClinician.id || loggedInClinician.email,
                                id,
                                {
                                    end,
                                    start,
                                    recurrenceRule,
                                    ending_date: canSetAvailabilityEndDate
                                        ? ending_date || ""
                                        : "",
                                }
                            );
                            await getClinician(loggedInClinician.email);
                            addToast("Availability updated", {
                                appearance: "success",
                            });

                            setShowAddAvailabilityDialog(false);

                            setShowReserveTimeWindowDialog(false);
                        } finally {
                            setShowLoader(false);
                        }
                    } else {
                        const crossesLocationBoundary =
                            scheduleCrossesLocationBoundary(
                                officeWindowSchedules,
                                schedule
                            );
                        addToast(
                            crossesLocationBoundary
                                ? "Availability window spans two different office locations — please split into separate slots"
                                : "Cannot set availability outside of Office window",
                            { appearance: "error" }
                        );
                    }
                    break;
                }

                if (
                    (schedule as IClientBooking).status !== BookingStatus.CONFIRMED
                ) {
                    break;
                }

                const {
                    clinician_id,
                    client_id,
                    id,
                    end,
                    start,
                    starting_date,
                    recurrenceRule,
                    ending_date = "",
                } = schedule as IClientBooking;
                setShowLoader(true);
                try {
                    await store.updateBooking(
                        clinician_id,
                        client_id,
                        id,
                        {
                            end,
                            start,
                            starting_date,
                            recurrenceRule,
                            ending_date,
                        },
                        {},
                        "confirmed",
                        loggedInUserEmail!
                    );

                    await getClinician(loggedInClinician.email);
                    addToast("Appointment updated", { appearance: "success" });
                } finally {
                    setShowLoader(false);
                }
                break;
            }
            case "remove": {
                setShowLoader(true);
                try {
                    let scheduleIdx = availabilitySchedules.findIndex(
                        (s) => s.id === schedule.id
                    );
                    if (scheduleIdx > -1) {
                        const updatedAvailabilitySchedules = [
                            ...availabilitySchedules,
                        ];
                        updatedAvailabilitySchedules.splice(scheduleIdx, 1);
                        await store.updateClinicianAvailability(
                            loggedInClinician.id || loggedInClinician.email,
                            AvailabilityStatus.AVAILABLE,
                            updatedAvailabilitySchedules
                        );
                        if ((schedule as IReservedSchedule).reservedFor) {
                            await store.deleteReservedAppointment(schedule);
                        }
                        setAvailabilitySchedules([...updatedAvailabilitySchedules]);
                    } else {
                        scheduleIdx = unavailabilitySchedules.findIndex(
                            (s) => s.id === schedule.id
                        );
                        const updatedAvailabilitySchedules = [
                            ...unavailabilitySchedules,
                        ];
                        updatedAvailabilitySchedules.splice(scheduleIdx, 1);
                        await store.updateClinicianAvailability(
                            loggedInClinician.id || loggedInClinician.email,
                            AvailabilityStatus.UNAVAILABLE,
                            updatedAvailabilitySchedules
                        );
                        setUnavailabilitySchedules([
                            ...updatedAvailabilitySchedules,
                        ]);
                    }
                } finally {
                    setShowLoader(false);
                }

                break;
            }
            default: {
                console.log({ action, schedule });
            }
        }
    };

    const findAvailableOfficeWindowToDelete = async (
        schedule: IAvailabilitySchedule
    ) => {
        const location = schedule.location || "";
        const office = schedule.office || "";
        const startTime = schedule.start || "";
        const endTime = schedule.end || "";

        return findAvailableSlotsThatMatchOfficeWindow(
            loggedInClinician?.available,
            location,
            office,
            startTime,
            endTime
        );
    };

    const manageOfficeWindow = async (
        action: "remove",
        schedule: IAvailabilitySchedule | IReservedSchedule
    ) => {
        switch (action) {
            case "remove": {
                setShowLoader(true);
                try {
                    const updatedOfficeWindows: IOfficeWindow[] =
                        loggedInClinician?.office_windows.filter(
                            (w) => w.id !== schedule.id
                        ) || [];

                    const clinicianName = `${loggedInClinician.firstname} ${loggedInClinician.lastname}`;

                    await store.updateClinicianOfficeWindows(
                        loggedInUserEmail as string,
                        updatedOfficeWindows,
                        "clinician",
                        schedule,
                        clinicianName,
                        "",
                        "clinician",
                        intakeSignature
                    );

                    const filteredSchedule = loggedInClinician?.available?.filter(
                        (item) => {
                            return findAvailableSlotsThatMatchOfficeWindow(
                                loggedInClinician?.available,
                                schedule.location || "",
                                schedule.office || "",
                                schedule.start || "",
                                schedule.end || ""
                            ).every((scheduleItem) => {
                                return item.id !== scheduleItem.id;
                            });
                        }
                    );
                    await store.updateClinicianAvailability(
                        loggedInUserEmail as string,
                        AvailabilityStatus.AVAILABLE,
                        filteredSchedule
                    );
                    if (loggedInUserEmail) {
                        getClinician(loggedInUserEmail);
                    }

                    // automatically add this window to available office windows for other clinicians
                    const customUUID = uuidv4();

                    try {
                        await store.createAvailableOfficeWindows({
                            office_window_data: {
                                start: new Date(
                                    schedule?.start as string
                                ).toISOString(),
                                end: new Date(schedule?.end as string).toISOString(),
                                location: schedule.location as string,
                                office: schedule.office as string,
                                id: customUUID,
                            },
                            loggedInUserEmail: loggedInUserEmail as string,
                            type: "automatic_creation",
                        });
                    } catch (err) {}
                } finally {
                    setShowLoader(false);
                }

                break;
            }
            default: {
                console.log({ action, schedule });
            }
        }
    };

    const tabItems = [
        {
            key: "caseload",
            label: "Caseload",
            onClick: () => setActiveTab("caseload"),
            children: (
                <Caseload
                    clinician={loggedInClinician}
                    caseloadClinician={clinicianData}
                    clients={caseloadClients}
                    setCaseLoadCountByFilter={setCaseLoadCountByFilter}
                    onUpdateCaseLoad={async () => {
                        await getClinician(loggedInClinician.email);
                    }}
                    clinicianServiceSelections={clinicianServiceSelection}
                />
            ),
            count: `${caseLoadCountByFilter} appointments`,
        },
        {
            key: "calendar",
            label: "Calendar",
            onClick: () => setActiveTab("calendar"),
            children: (
                <div className={styles.calendarWrapper}>
                    <Alert
                        type="info"
                        className={styles.calendarAlertWrapper}
                        contentClassName={styles.calendarAlert}
                    >
                        <p>
                            You can manage your availability, reserve time windows
                            for clients, and mark time off all from your calendar
                            view.
                        </p>
                        <div className={styles.calendarAlertActions}>
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <Button
                                        iconRight={
                                            <ChevronDownIcon stroke="var(--color-white)" />
                                        }
                                    >
                                        Manage calendar
                                    </Button>
                                </Dropdown.Trigger>
                                <Dropdown.Content align="end">
                                    <Dropdown.Item
                                        onClick={() =>
                                            setShowAddAvailabilityDialog(true)
                                        }
                                    >
                                        Add available time slots
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() =>
                                            setShowReserveTimeWindowDialog(true)
                                        }
                                    >
                                        Reserve time window
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => setShowTimeOffDialog(true)}
                                    >
                                        Time-off
                                    </Dropdown.Item>
                                    {loggedInClinician.is_supervisor && (
                                        <Dropdown.Item
                                            onClick={() =>
                                                setShowAddIntakeSessionDialog(true)
                                            }
                                        >
                                            Add Supervisor Intake Session
                                            Availability
                                        </Dropdown.Item>
                                    )}
                                </Dropdown.Content>
                            </Dropdown>
                        </div>
                    </Alert>

                    <div className={styles.calendarContent}>
                        <div className={styles.calendarHeader}>
                            <div className={styles.calendarHeaderLeft}>
                                <ToggleButton
                                    onToggle={() => {
                                        setCalendarSwitch({
                                            ...calendarSwitch,
                                            [CalendarId.AVAILABILITY]:
                                                !calendarSwitch[
                                                    CalendarId.AVAILABILITY
                                                ],
                                        });
                                    }}
                                    checked={calendarSwitch[CalendarId.AVAILABILITY]}
                                    label={CalendarName.AVAILABILITY}
                                    variant="availability"
                                />
                                <ToggleButton
                                    onToggle={() => {
                                        setCalendarSwitch({
                                            ...calendarSwitch,
                                            [CalendarId.OFFICE]:
                                                !calendarSwitch[CalendarId.OFFICE],
                                        });
                                    }}
                                    checked={calendarSwitch[CalendarId.OFFICE]}
                                    label={CalendarName.OFFICE}
                                    variant="office-window"
                                />
                                <ToggleButton
                                    onToggle={() => {
                                        setCalendarSwitch({
                                            ...calendarSwitch,
                                            [CalendarId.HELD_BOOKINGS]:
                                                !calendarSwitch[
                                                    CalendarId.HELD_BOOKINGS
                                                ],
                                        });
                                    }}
                                    checked={
                                        calendarSwitch[CalendarId.HELD_BOOKINGS]
                                    }
                                    label={CalendarName.HELD_BOOKINGS}
                                    variant="held"
                                />
                                <ToggleButton
                                    onToggle={() => {
                                        setCalendarSwitch({
                                            ...calendarSwitch,
                                            [CalendarId.CONFIRMED_BOOKINGS]:
                                                !calendarSwitch[
                                                    CalendarId.CONFIRMED_BOOKINGS
                                                ],
                                        });
                                    }}
                                    checked={
                                        calendarSwitch[CalendarId.CONFIRMED_BOOKINGS]
                                    }
                                    label={CalendarName.CONFIRMED_BOOKINGS}
                                    variant="confirmed"
                                />
                                {loggedInClinician.is_supervisor && (
                                    <ToggleButton
                                        onToggle={() => {
                                            setCalendarSwitch({
                                                ...calendarSwitch,
                                                [CalendarId.INTAKE_SESSION_AVAILABILITY]:
                                                    !calendarSwitch[
                                                        CalendarId
                                                            .INTAKE_SESSION_AVAILABILITY
                                                    ],
                                            });
                                        }}
                                        checked={
                                            calendarSwitch[
                                                CalendarId
                                                    .INTAKE_SESSION_AVAILABILITY
                                            ]
                                        }
                                        label={
                                            CalendarName.INTAKE_SESSION_AVAILABILITY
                                        }
                                        variant="intake"
                                    />
                                )}
                            </div>
                            <div className={styles.calendarHeaderRight}>
                                {activeSwitch === "calendar" && (
                                    <div className={styles.calendarToggleBar}>
                                        <Dropdown>
                                            <Dropdown.Trigger>
                                                <FilterButton
                                                    text={getViewLabel()}
                                                />
                                            </Dropdown.Trigger>
                                            <Dropdown.Content width="100px">
                                                {viewOptions.map((option) => (
                                                    <Dropdown.Item
                                                        key={option.value}
                                                        onClick={() =>
                                                            goToView(option.value)
                                                        }
                                                    >
                                                        {option.label}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Content>
                                        </Dropdown>

                                        <div className={styles.calendarToggleDate}>
                                            <Button
                                                onClick={goToPrevious}
                                                variant="normal"
                                                className={
                                                    styles.calendarToggleButton
                                                }
                                            >
                                                <ChevronBackIcon />
                                            </Button>

                                            <span className={styles.calendarDate}>
                                                {formatDateRange()}
                                            </span>
                                            <Button
                                                variant="normal"
                                                onClick={goToNext}
                                                className={
                                                    styles.calendarToggleButton
                                                }
                                            >
                                                <ChevronRightIcon />
                                            </Button>
                                        </div>

                                        <Button
                                            variant="secondary"
                                            onClick={goToToday}
                                        >
                                            Today
                                        </Button>
                                    </div>
                                )}
                                <Switch
                                    switchOptions={[
                                        {
                                            title: "List view",
                                            key: "list",
                                            onClick: () => {
                                                handleSwitch("list");
                                            },
                                        },
                                        {
                                            title: "Calendar view",
                                            key: "calendar",
                                            onClick: () => {
                                                handleSwitch("calendar");
                                            },
                                        },
                                    ]}
                                    activeSwitch={activeSwitch}
                                />
                            </div>
                        </div>
                        <div className={styles.calendarContainer}>
                            {activeSwitch === "list" && (
                                <Calendar
                                    schedules={[
                                        ...officeWindowSchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId.OFFICE
                                                    ],
                                                action: "clinician_deleting_office_window" as DeleteActions,
                                            };
                                        }),
                                        ...availabilitySchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId.AVAILABILITY
                                                    ],
                                                canEdit: true,
                                                action: "clinician_deleting_availability" as DeleteActions,
                                            };
                                        }),
                                        ...heldBookingSchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId.HELD_BOOKINGS
                                                    ],
                                            };
                                        }),
                                        ...(
                                            confirmedBookingSchedules as IClientBooking[]
                                        )
                                            .filter((w) => !w.is_paused)
                                            .map((w) => {
                                                return {
                                                    ...w,
                                                    isVisible:
                                                        calendarSwitch[
                                                            CalendarId
                                                                .CONFIRMED_BOOKINGS
                                                        ],
                                                    canEdit: true,
                                                };
                                            }),
                                        ...availableIntakeSessionSchedules.map(
                                            (w) => {
                                                return {
                                                    ...w,
                                                    isVisible:
                                                        calendarSwitch[
                                                            CalendarId
                                                                .INTAKE_SESSION_AVAILABILITY
                                                        ],
                                                    canEdit: true,
                                                    isIntakeSession: true,
                                                    isIntakeSessionAvailable: true,
                                                    calendarId:
                                                        CalendarId.INTAKE_SESSION_AVAILABILITY,
                                                };
                                            }
                                        ),
                                        ...bookedIntakeSessionSchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId
                                                            .INTAKE_SESSION_AVAILABILITY
                                                    ],
                                                canEdit: w.status === "confirmed",
                                                isIntakeSession: true,
                                                isIntakeSessionBooked: true,
                                                calendarId:
                                                    CalendarId.INTAKE_SESSION_AVAILABILITY,
                                                bookingId: (w as any).bookingId,
                                                client_id: (w as any).client_id,
                                            };
                                        }),
                                    ]}
                                    onUpdateSchedule={(schedule) => {
                                        manageAvailability("update", schedule);
                                    }}
                                    onRemoveSchedule={(schedule) => {
                                        if (
                                            (schedule as any).isIntakeSession ||
                                            schedule.calendarId ===
                                                CalendarId.INTAKE_SESSION_AVAILABILITY
                                        ) {
                                            if (
                                                (schedule as any)
                                                    .isIntakeSessionBooked
                                            ) {
                                                setSelectedIntakeSchedule(schedule);
                                                setShowCancelIntakeDialog(true);
                                            } else {
                                                handleDeleteIntakeSession(schedule);
                                            }
                                        } else {
                                            manageAvailability("remove", schedule);
                                        }
                                    }}
                                    onRemoveOfficeWindow={(schedule) => {
                                        manageOfficeWindow("remove", schedule);
                                    }}
                                    onSelectSchedule={async (schedule) => {
                                        return await findAvailableOfficeWindowToDelete(
                                            schedule
                                        );
                                    }}
                                    loggedInClinician={loggedInClinician}
                                    officeSchedulesInReview={officeSchedulesInReview}
                                    setRefreshKey={setRefreshKey}
                                    onEditIntakeSession={(schedule) => {
                                        setDraftIntakeSession({
                                            from: schedule.start
                                                ? new Date(schedule.start)
                                                : new Date(),
                                            to: schedule.end
                                                ? new Date(schedule.end)
                                                : new Date(),
                                            serviceDeliveryOption:
                                                (schedule.serviceDeliveryOption as ServiceDeliveryOption) ||
                                                "in_person",
                                            practice_id:
                                                (schedule as any).practice_id || "",
                                        });

                                        setSelectedIntakeSchedule(schedule);

                                        if (
                                            (schedule as any).isIntakeSessionBooked
                                        ) {
                                            setIntakeActionReason("");
                                            setShowEditIntakeSessionDialog(true);
                                        } else {
                                            setShowEditAvailableIntakeDialog(true);
                                        }
                                    }}
                                    intakeSessionAvailability={
                                        intakeSessionAvailability
                                    }
                                />
                            )}
                            {activeSwitch === "calendar" && (
                                <CalendarBoxView
                                    schedules={[
                                        ...officeWindowSchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId.OFFICE
                                                    ],
                                                action: "clinician_deleting_office_window" as DeleteActions,
                                            };
                                        }),
                                        ...availabilitySchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId.AVAILABILITY
                                                    ],
                                                canEdit: true,
                                                action: "clinician_deleting_availability" as DeleteActions,
                                            };
                                        }),
                                        ...heldBookingSchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId.HELD_BOOKINGS
                                                    ],
                                            };
                                        }),
                                        ...(
                                            confirmedBookingSchedules as IClientBooking[]
                                        )
                                            .filter((w) => !w.is_paused)
                                            .map((w) => {
                                                return {
                                                    ...w,
                                                    isVisible:
                                                        calendarSwitch[
                                                            CalendarId
                                                                .CONFIRMED_BOOKINGS
                                                        ],
                                                    canEdit: true,
                                                };
                                            }),
                                        ...availableIntakeSessionSchedules.map(
                                            (w) => {
                                                return {
                                                    ...w,
                                                    isVisible:
                                                        calendarSwitch[
                                                            CalendarId
                                                                .INTAKE_SESSION_AVAILABILITY
                                                        ],
                                                    canEdit: true,

                                                    isIntakeSession: true,
                                                    isIntakeSessionAvailable: true,
                                                    calendarId:
                                                        CalendarId.INTAKE_SESSION_AVAILABILITY,
                                                };
                                            }
                                        ),
                                        ...bookedIntakeSessionSchedules.map((w) => {
                                            return {
                                                ...w,
                                                isVisible:
                                                    calendarSwitch[
                                                        CalendarId
                                                            .INTAKE_SESSION_AVAILABILITY
                                                    ],
                                                canEdit: w.status === "confirmed",

                                                isIntakeSession: true,
                                                isIntakeSessionBooked: true,
                                                calendarId:
                                                    CalendarId.INTAKE_SESSION_AVAILABILITY,
                                                bookingId: (w as any).bookingId,
                                                client_id: (w as any).client_id,
                                            };
                                        }),
                                    ]}
                                    onUpdateSchedule={(schedule) => {
                                        manageAvailability("update", schedule);
                                    }}
                                    onRemoveSchedule={(schedule) => {
                                        if (
                                            (schedule as any).isIntakeSession ||
                                            schedule.calendarId ===
                                                CalendarId.INTAKE_SESSION_AVAILABILITY
                                        ) {
                                            if (
                                                (schedule as any)
                                                    .isIntakeSessionBooked
                                            ) {
                                                setSelectedIntakeSchedule(schedule);
                                                setShowCancelIntakeDialog(true);
                                            } else {
                                                handleDeleteIntakeSession(schedule);
                                            }
                                        } else {
                                            manageAvailability("remove", schedule);
                                        }
                                    }}
                                    onRemoveOfficeWindow={(schedule) => {
                                        manageOfficeWindow("remove", schedule);
                                    }}
                                    onSelectSchedule={async (schedule) => {
                                        return await findAvailableOfficeWindowToDelete(
                                            schedule
                                        );
                                    }}
                                    loggedInClinician={loggedInClinician}
                                    officeSchedulesInReview={officeSchedulesInReview}
                                    currentView={currentView}
                                    currentDate={currentDate}
                                    onViewChange={setCurrentView}
                                    onDateChange={setCurrentDate}
                                    onEditIntakeSession={(schedule) => {
                                        setDraftIntakeSession({
                                            from: schedule.start
                                                ? new Date(schedule.start)
                                                : new Date(),
                                            to: schedule.end
                                                ? new Date(schedule.end)
                                                : new Date(),
                                            serviceDeliveryOption:
                                                (schedule.serviceDeliveryOption as ServiceDeliveryOption) ||
                                                "in_person",
                                            practice_id:
                                                (schedule as any).practice_id || "",
                                        });

                                        setSelectedIntakeSchedule(schedule);

                                        if (
                                            (schedule as any).isIntakeSessionBooked
                                        ) {
                                            setIntakeActionReason("");
                                            setShowEditIntakeSessionDialog(true);
                                        } else {
                                            setShowEditAvailableIntakeDialog(true);
                                        }
                                    }}
                                    intakeSessionAvailability={
                                        intakeSessionAvailability
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            ),
        },

        {
            key: "history",
            label: "History",
            children: (
                <HistoryWrapper loggedInUserEmailProp={loggedInUserEmailProp} />
            ),
        },
    ];

    // Dialog save functions
    const handleAddAvailability = () => {
        const schedule: IAvailabilitySchedule = {
            id: uuidv4(),
            title: `${
                draftAvailability.status === AvailabilityStatus.AVAILABLE
                    ? "Available"
                    : "Unavailable"
            }`,
            calendarId:
                draftAvailability.status === AvailabilityStatus.AVAILABLE
                    ? CalendarId.AVAILABILITY
                    : CalendarId.UNAVAILABLITY,
            start: draftAvailability.from.toISOString(),
            end: draftAvailability.to.toISOString(),
            category: "time",
            recurrenceRule: draftAvailability.recurrenceRule,
            availableService: draftAvailability.availableService,
        };

        if (
            draftAvailability.endDate &&
            loggedInClinician.credential === ClinicianCredential.INTERN &&
            draftAvailability.recurrenceRule !== RecurrenceRule.ONE_OFF
        ) {
            schedule.ending_date = draftAvailability.endDate?.toISOString();
        }

        manageAvailability("add", schedule);
    };

    const handleReserveTimeWindow = async () => {
        if (!draftReservedWindow.reservedFor) {
            addToast("Enter the clients email", {
                appearance: "error",
            });
            return;
        }
        if (
            loggedInClinician?.departments?.length > 1 &&
            !draftReservedWindow.practice_id
        ) {
            addToast("Select a practice", {
                appearance: "error",
            });
            return;
        }
        const reserveWindowPracticeId =
            draftReservedWindow.practice_id ||
            allDepartmentsAndServices[loggedInClinician?.departments?.[0]]
                ?.practice_id ||
            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string) ||
            "";
        const schedule: IReservedSchedule = {
            id: uuidv4(),
            title: `Reserved for ${draftReservedWindow.reservedFor}`,
            calendarId:
                draftReservedWindow.status === AvailabilityStatus.AVAILABLE
                    ? CalendarId.AVAILABILITY
                    : CalendarId.UNAVAILABLITY,
            start: draftReservedWindow.from.toISOString(),
            end: draftReservedWindow.to.toISOString(),
            category: "time",
            recurrenceRule: draftReservedWindow.recurrenceRule,
            availableService: draftReservedWindow.availableService,
            serviceDeliveryOption: draftReservedWindow.serviceDeliveryOption,
            reservedFor: draftReservedWindow.reservedFor.toLowerCase().trim(),
            clinicianId: loggedInClinician.id || loggedInClinician.email,
            clinicianName: getClinicianName(loggedInClinician),
            practice_id: reserveWindowPracticeId,
        };
        try {
            await manageAvailability("add", schedule);
        } catch {
            addToast("Failed to reserve time window", {
                appearance: "error",
            });
        }
    };

    const handleTimeOff = () => {
        const schedule: IAvailabilitySchedule = {
            id: uuidv4(),
            title: "On Vacation",
            calendarId: CalendarId.UNAVAILABLITY,
            start: draftVacation.startingDate.toISOString(),
            end: draftVacation.endingDate.toISOString(),
            updated_at: draftVacation.updatedAt.toISOString(),
            category: "time",
        };
        manageAvailability("add", schedule, AvailabilityStatus.UNAVAILABLE);
        setShowTimeOffDialog(false);
    };

    const handleAddIntakeSession = async (): Promise<boolean> => {
        setShowIntakeActionLoader(true);
        try {
            if (selectedIntakeSchedule && showEditIntakeSessionDialog) {
                return await handleEditIntakeSession();
            } else {
                const intakeSessionPracticeId =
                    draftIntakeSession.practice_id ||
                    (intakeSessionPracticeOptions.length === 1
                        ? intakeSessionPracticeOptions[0].value
                        : "");
                if (!intakeSessionPracticeId) {
                    addToast("Select a practice for this intake session", {
                        appearance: "error",
                    });
                    return false;
                }

                const now = moment();
                const startMoment = moment(draftIntakeSession.from);
                if (!startMoment.isAfter(now)) {
                    addToast("Cannot create intake session in the past", {
                        appearance: "error",
                    });
                    return false;
                }

                const sessionId = uuidv4();
                const newIntakeSession: IIntakeAvailability = {
                    id: sessionId,
                    start_time: draftIntakeSession.from.toISOString(),
                    end_time: draftIntakeSession.to.toISOString(),
                    recurrence: RecurrenceRule.ONE_OFF,
                    service_delivery_option: "remote",
                    location: "Remote",
                    status: "available",
                    office: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
                    created_at: new Date(),
                    clinician_display_name: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
                    supervisor_id: loggedInClinician?.id || "",
                    practice_id: intakeSessionPracticeId,
                };

                // Check for conflicts with existing schedules
                if (checkIntakeSessionConflicts(newIntakeSession)) {
                    return false;
                }

                if (loggedInClinician.id || loggedInClinician.email) {
                    try {
                        await store.updateClinicianData({
                            clinician_id:
                                loggedInClinician.id || loggedInClinician.email,
                            changes: {
                                available_for_intake_sessions:
                                    arrayUnion(newIntakeSession),
                            },
                        });
                        if (loggedInUserEmail) {
                            await getClinician(loggedInUserEmail);
                        }
                        addToast("Intake session availability added", {
                            appearance: "success",
                        });
                        setShowAddIntakeSessionDialog(false);
                        setSelectedIntakeSchedule(null);
                        return true;
                    } catch (error) {
                        console.error(
                            "Failed to save intake session availability",
                            error
                        );
                        addToast("Failed to save intake session availability", {
                            appearance: "error",
                        });
                        return false;
                    }
                }
                return false;
            }
        } finally {
            setShowIntakeActionLoader(false);
        }
    };

    const handleEditIntakeSession = async (): Promise<boolean> => {
        if (!selectedIntakeSchedule || !selectedIntakeSchedule.id) return false;

        const intakeSessionPracticeId =
            draftIntakeSession.practice_id ||
            (intakeSessionPracticeOptions.length === 1
                ? intakeSessionPracticeOptions[0].value
                : "");
        if (!intakeSessionPracticeId) {
            addToast("Select a practice for this intake session", {
                appearance: "error",
            });
            return false;
        }

        const newIntakeSession: IIntakeAvailability = {
            id: selectedIntakeSchedule.id as string,
            start_time: draftIntakeSession.from.toISOString(),
            end_time: draftIntakeSession.to.toISOString(),
            recurrence: RecurrenceRule.ONE_OFF,
            service_delivery_option: "remote" as ServiceDeliveryOption,
            location: "Remote",
            status: "available",
            office: `${loggedInClinician?.firstname || ""} ${
                loggedInClinician?.lastname || ""
            }`,
            clinician_display_name: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
            supervisor_id: loggedInClinician?.id || "",
            practice_id: intakeSessionPracticeId,
            supervisees_display_name:
                (selectedIntakeSchedule as any).supervisees_display_name || "",
            created_at: (selectedIntakeSchedule as any).created_at || new Date(),
        };

        if ((selectedIntakeSchedule as any).isIntakeSessionBooked) {
            await handleRescheduleIntakeSession(
                selectedIntakeSchedule,
                newIntakeSession
            );
            return true;
        } else {
            setShowIntakeActionLoader(true);
            try {
                // Check for conflicts, excluding the current session being edited
                if (
                    checkIntakeSessionConflicts(
                        newIntakeSession,
                        selectedIntakeSchedule.id as string
                    )
                ) {
                    return false;
                }

                const currentSessions =
                    loggedInClinician.available_for_intake_sessions || [];
                const updatedSessions = currentSessions.map((slot) =>
                    slot.id === selectedIntakeSchedule.id ? newIntakeSession : slot
                );

                if (loggedInClinician.id || loggedInClinician.email) {
                    try {
                        await store.updateClinicianData({
                            clinician_id:
                                loggedInClinician.id || loggedInClinician.email,
                            changes: {
                                available_for_intake_sessions: updatedSessions,
                            },
                        });
                        if (loggedInUserEmail) {
                            getClinician(loggedInUserEmail);
                        }
                        addToast("Intake session availability updated", {
                            appearance: "success",
                        });
                        setShowEditAvailableIntakeDialog(false);
                        setSelectedIntakeSchedule(null);
                        return true;
                    } catch (error) {
                        console.error(
                            "Failed to update intake session availability",
                            error
                        );
                        addToast("Failed to update intake session availability", {
                            appearance: "error",
                        });
                        return false;
                    }
                }

                return false;
            } finally {
                setShowIntakeActionLoader(false);
            }
        }
    };

    const handleDeleteIntakeSession = async (schedule: IAvailabilitySchedule) => {
        setShowIntakeActionLoader(true);
        // Delete only sessions that have not been booked (available_for_intake_sessions)
        const scheduleId = schedule.id as string;
        if (!scheduleId) {
            console.error("Cannot delete: schedule.id is missing", schedule);
            addToast("Error: Cannot delete intake session", {
                appearance: "error",
            });
            return;
        }

        const currentSessions =
            loggedInClinician.available_for_intake_sessions || [];
        const updatedSessions = currentSessions.filter(
            (slot) => slot.id !== scheduleId
        );

        if (updatedSessions.length === currentSessions.length) {
            console.warn(
                "No session was deleted. Schedule ID:",
                scheduleId,
                "Available IDs:",
                currentSessions.map((s) => s.id)
            );
            addToast("Error: Session not found for deletion", {
                appearance: "error",
            });
            return;
        }

        if (loggedInClinician.id || loggedInClinician.email) {
            try {
                await store.updateClinicianData({
                    clinician_id: loggedInClinician.id || loggedInClinician.email,
                    changes: {
                        available_for_intake_sessions: updatedSessions,
                    },
                });
                if (loggedInUserEmail) {
                    getClinician(loggedInUserEmail);
                }
                addToast("Intake session availability deleted", {
                    appearance: "success",
                });
            } catch (error) {
                console.error("Failed to delete intake session availability", error);
                addToast("Error: Failed to save changes", {
                    appearance: "error",
                });
            } finally {
                setShowIntakeActionLoader(false);
            }
        }
    };

    const handleRescheduleIntakeSession = async (
        schedule: IAvailabilitySchedule,
        newIntakeSession: IIntakeAvailability
    ) => {
        setShowIntakeActionLoader(true);
        try {
            if (!(schedule as any).isIntakeSessionBooked) {
                console.warn(
                    "handleRescheduleIntakeSession called for non-booked session, this should not happen"
                );
                return;
            }

            const bookingId = (schedule as any).bookingId;

            let bookingStatus: BookingStatus = BookingStatus.CONFIRMED;

            const client = await store.getClientById((schedule as any).client_id);
            if (client) {
                const isHeld = (client.held_bookings || []).some(
                    (b) => b.id === bookingId
                );
                const isConfirmed = (client.confirmed_bookings || []).some(
                    (b) => b.id === bookingId
                );

                if (isHeld) {
                    bookingStatus = BookingStatus.HELD;
                } else if (isConfirmed) {
                    bookingStatus = BookingStatus.CONFIRMED;
                }
            }

            const booking =
                client?.held_bookings.find((b) => b.id === bookingId) ||
                client?.confirmed_bookings.find((b) => b.id === bookingId);

            // Check for conflicts (excluding the current booking)
            if (
                checkIntakeSessionConflicts(newIntakeSession, undefined, bookingId)
            ) {
                return;
            }

            const previousIntake = booking?.supervisor_intake_session;

            const currentSessions =
                loggedInClinician.available_for_intake_sessions || [];

            const sessionToUpdate = currentSessions.find(
                (s) => s.booking_id === bookingId || ""
            );

            if (sessionToUpdate) {
                const updatedSessions = currentSessions.map((session) => {
                    if (session.id === sessionToUpdate.id) {
                        return {
                            ...session,
                            start_time: newIntakeSession.start_time,
                            end_time: newIntakeSession.end_time,
                        };
                    }
                    return session;
                });

                try {
                    await store.updateClinicianData({
                        clinician_id:
                            loggedInClinician.id || loggedInClinician.email,
                        changes: {
                            available_for_intake_sessions: updatedSessions,
                        },
                    });
                    if (loggedInUserEmail) {
                        await getClinician(loggedInUserEmail);
                    }
                } catch (error) {
                    console.error("Failed to update intake session", error);
                }
            }

            if (client && booking && previousIntake) {
                const clientIntakeSessionHistoryEntry: IIntakeSessionHistory = {
                    ...previousIntake,
                    booking_id: booking.id,
                    supervisor_id: previousIntake.supervisor_id,
                    start_time: previousIntake.start_time,
                    end_time: previousIntake.end_time,
                    status: previousIntake.status,
                    actioned_by: loggedInUserEmail || "",
                    action: "rescheduled",
                    actioned_on: new Date(),
                    reason: intakeActionReason || "",
                    practice_id: previousIntake.practice_id || "",
                    clinician_display_name:
                        previousIntake.clinician_display_name || "",
                    service_delivery_option:
                        previousIntake.service_delivery_option || "remote",
                    location: previousIntake.location || "Remote",
                    recurrence: previousIntake.recurrence || RecurrenceRule.ONE_OFF,
                    booking_requested_service: booking.requested_service || "",
                    booking_clinician_display_name:
                        booking.clinician_display_name || "",
                    booking_start: booking.start || "",
                    booking_starting_date: booking.starting_date || "",
                    booking_practice_id: booking.practice_id || "",
                };

                try {
                    await store.updateClientData({
                        client_id: client.id!,
                        changes: {
                            intake_session_history: arrayUnion(
                                clientIntakeSessionHistoryEntry
                            ),
                        },
                        last_updated_by: loggedInUserEmail || "",
                    });
                } catch (error) {
                    console.error(
                        "Failed to update client intake session history for reschedule",
                        error
                    );
                }
            }

            const signatureEmail =
                client?.assigned_to || client?.secondary_assignee || "";

            const confirmedApptSignature =
                allEmailSignatures[signatureEmail as string];

            const success = await store.updateBooking(
                booking?.clinician_id || "",
                booking?.client_id || "",
                booking?.id || "",
                {
                    supervisor_intake_session: {
                        ...(previousIntake ?? {}),
                        id: previousIntake?.id ?? sessionToUpdate?.id ?? "",
                        supervisor_id: previousIntake?.supervisor_id || "",
                        practice_id: newIntakeSession?.practice_id || "",
                        service_delivery_option:
                            newIntakeSession.service_delivery_option,
                        start_time: newIntakeSession.start_time,
                        end_time: newIntakeSession.end_time,
                        recurrence: RecurrenceRule.ONE_OFF,
                        created_at: new Date(),
                        status:
                            bookingStatus === BookingStatus.HELD
                                ? "held"
                                : "confirmed",
                        completed_at: previousIntake?.completed_at || null,
                        location: newIntakeSession.location || "Remote",
                        office:
                            newIntakeSession.office ||
                            `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
                        clinician_display_name: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
                    },
                },
                {},
                bookingStatus === BookingStatus.HELD ? "held" : "confirmed",
                loggedInUserEmail!,
                confirmedApptSignature,
                allDepartmentsAndServices,
                intakeSignature,
                "supervisor_intake_session"
            );

            if (!success) {
                addToast("Failed to reschedule intake session", {
                    appearance: "error",
                });
                return;
            }

            if (loggedInClinician.email) {
                await getClinician(loggedInClinician.email);
            }

            addToast("Intake session rescheduled", {
                appearance: "success",
            });
            setShowEditIntakeSessionDialog(false);
            setSelectedIntakeSchedule(null);
            setIntakeActionReason("");
        } finally {
            setShowIntakeActionLoader(false);
        }
    };

    const handleCancelIntakeSession = async (schedule: IAvailabilitySchedule) => {
        setShowIntakeActionLoader(true);
        try {
            const bookingId = (schedule as any).bookingId;

            let bookingStatus: BookingStatus = BookingStatus.CONFIRMED;

            const client = await store.getClientById((schedule as any).client_id);
            if (client) {
                const isHeld = (client.held_bookings || []).some(
                    (b) => b.id === bookingId
                );
                const isConfirmed = (client.confirmed_bookings || []).some(
                    (b) => b.id === bookingId
                );

                if (isHeld) {
                    bookingStatus = BookingStatus.HELD;
                } else if (isConfirmed) {
                    bookingStatus = BookingStatus.CONFIRMED;
                }
            }

            const booking =
                client?.held_bookings.find((b) => b.id === bookingId) ||
                client?.confirmed_bookings.find((b) => b.id === bookingId);

            const previousIntake = booking?.supervisor_intake_session;

            const currentSessions =
                loggedInClinician.available_for_intake_sessions || [];

            const sessionToUpdate = currentSessions.find(
                (s) => s.booking_id === bookingId || ""
            );

            if (sessionToUpdate) {
                const updatedSessions = currentSessions.map((session) => {
                    if (session.id === sessionToUpdate.id) {
                        return {
                            ...session,
                            client_id: "",
                            booking_id: "",
                            practice_id: "",
                            client_display_name: "",
                            status: "available",
                        };
                    }
                    return session;
                });

                try {
                    await store.updateClinicianData({
                        clinician_id:
                            loggedInClinician.id || loggedInClinician.email,
                        changes: {
                            available_for_intake_sessions: updatedSessions,
                        },
                    });
                    if (loggedInUserEmail) {
                        getClinician(loggedInUserEmail);
                    }
                } catch (error) {
                    console.error("Failed to update intake session", error);
                }
            }

            if (client && booking && previousIntake) {
                const clientIntakeSessionHistoryEntry: IIntakeSessionHistory = {
                    ...previousIntake,
                    booking_id: booking.id,
                    supervisor_id: previousIntake.supervisor_id,
                    start_time: previousIntake.start_time,
                    end_time: previousIntake.end_time,
                    status: previousIntake.status,
                    actioned_by: loggedInUserEmail || "",
                    action: "cancelled",
                    actioned_on: new Date(),
                    practice_id: previousIntake.practice_id || "",
                    clinician_display_name:
                        previousIntake.clinician_display_name || "",
                    service_delivery_option:
                        previousIntake.service_delivery_option || "remote",
                    location: previousIntake.location || "Remote",
                    recurrence: previousIntake.recurrence || RecurrenceRule.ONE_OFF,
                    reason: intakeActionReason || "",
                    booking_requested_service: booking.requested_service || "",
                    booking_clinician_display_name:
                        booking.clinician_display_name || "",
                    booking_start: booking.start || "",
                    booking_starting_date: booking.starting_date || "",
                    booking_practice_id: booking.practice_id || "",
                };

                try {
                    await store.updateClientData({
                        client_id: client.id!,
                        changes: {
                            intake_session_history: arrayUnion(
                                clientIntakeSessionHistoryEntry
                            ),
                        },
                        last_updated_by: loggedInUserEmail || "",
                    });
                } catch (error) {
                    console.error(
                        "Failed to update client intake session history for reschedule",
                        error
                    );
                }
            }

            const success = await store.updateBooking(
                booking?.clinician_id || "",
                booking?.client_id || "",
                booking?.id || "",
                {
                    supervisor_intake_session: null,
                },
                {},
                bookingStatus === BookingStatus.HELD ? "held" : "confirmed",
                loggedInUserEmail!
            );

            if (!success) {
                addToast("Failed to cancel intake session", {
                    appearance: "error",
                });
                return;
            }

            if (loggedInClinician.email) {
                await getClinician(loggedInClinician.email);
            }

            addToast("Intake session cancelled", {
                appearance: "success",
            });
            setShowCancelIntakeDialog(false);
            setSelectedIntakeSchedule(null);
            setIntakeActionReason("");
        } finally {
            setShowIntakeActionLoader(false);
        }
    };

    const handleSave = async () => {
        setShowLoader(true);
        try {
            await store.updateClinicianData({
                clinician_id: loggedInClinician.id || "",
                changes: {
                    contact_info_client: clientContact,
                    contact_info_personal: personalContact,
                    ideal_caseload: Number(idealCaseload),
                },
            });
            addToast("Info updated successfully", {
                appearance: "success",
            });
            setIsEditingClientContact(false);
            setIsEditingPersonalContact(false);
            setIsEditingIdealCaseload(false);
        } finally {
            setShowLoader(false);
        }
    };

    const isDelegateAccess =
        delegateUserEmail && delegateUserEmail !== loggedInUserEmail;

    const locations = loggedInClinician.office_windows?.map((w) => w.location);
    const uniqueLocations = Array.from(new Set(locations));

    React.useEffect(() => {
        if (loggedInUserEmail) {
            getClinician(loggedInUserEmail);
        }
    }, [loggedInUserEmail]);

    React.useEffect(() => {
        store
            .getAllExperienceLevels()
            .then(setAllExperienceLevelOptions)
            .catch(() =>
                addToast("Unable to load credential expiration information", {
                    appearance: "error",
                })
            );
    }, [addToast]);

    React.useEffect(() => {
        const getOfficeSchedulesInReview = async () => {
            if (
                !loggedInClinician?.office_windows ||
                loggedInClinician?.office_windows.length === 0
            ) {
                setOfficeSchedulesInReview([]);
                return;
            }

            setShowLoader(true);
            try {
                const officeSchedulesInReview =
                    await store.findOfficeWindowRequestsByIds(
                        loggedInClinician.office_windows.map((w) => w.id)
                    );
                const finalOfficeSchedulesInReview = officeSchedulesInReview.filter(
                    (schedule) => schedule.status !== OfficeRequestStatus.REJECTED
                );
                setOfficeSchedulesInReview(finalOfficeSchedulesInReview);
            } finally {
                setShowLoader(false);
            }
        };

        getOfficeSchedulesInReview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey, loggedInClinician.office_windows]);

    React.useEffect(() => {
        const fetchCaseloadClients = async (clientIds: string[]) => {
            setShowLoader(true);
            try {
                const clients = await store.fetchClientsByIds(
                    uniq(clientIds.filter((id) => id))
                );
                setCaseloadClients(
                    clients.reduce((prev, curr) => {
                        if (curr !== null) {
                            prev[curr.id] = curr;
                        }
                        return prev;
                    }, {} as { [id: string]: IClient })
                );
            } finally {
                setShowLoader(false);
            }
        };

        const clientBookings = [
            ...(clinicianData.confirmed || []),
            ...(clinicianData.held || []),
        ];

        const supervisorIntakeClientIds = (
            clinicianData.available_for_intake_sessions || []
        )
            .filter((session) => session.client_id)
            .map((session) => session.client_id!);

        const allClientIds = [
            ...clientBookings.map((c) => c.client_id),
            ...supervisorIntakeClientIds,
        ];

        if (allClientIds.length > 0) {
            fetchCaseloadClients(allClientIds);
        } else {
            setCaseloadClients({});
        }
    }, [
        clinicianData.confirmed,
        clinicianData.held,
        clinicianData.available_for_intake_sessions,
    ]);

    React.useEffect(() => {
        if (loggedInUserEmail) {
            getClinician(loggedInUserEmail);
        }
    }, [loggedInUserEmail]);

    return (
        <div className="v2-page">
            <Loader show={showLoader || showIntakeActionLoader} />
            <ExperienceLevels
                allExperienceLevelOptions={allExperienceLevelOptions}
                clinician={loggedInClinician}
                show={showExpLevels}
                handleClose={() => setShowExpLevels(false)}
                onSave={(experienceLevels) =>
                    setClinicianData((current) => ({
                        ...current,
                        experience_levels: experienceLevels,
                    }))
                }
            />
            {isDelegateAccess && (
                <Alert className={styles.alert}>
                    <div>
                        Viewing <strong>{loggedInClinician.fullname}'s</strong>{" "}
                        caseload. To view your caseload, please go to your
                        availability page
                    </div>
                </Alert>
            )}
            <div className={styles.header}>
                <div className={styles.headerTop}>
                    <div className={styles.headerLeft}>
                        <BackButton
                            onClick={() => history.goBack()}
                            className={styles.clinicianBackButton}
                        />
                        <div className={styles.clinicianInfoContainer}>
                            <div className={styles.clinicianName}>
                                <p>
                                    {loggedInClinician.firstname}{" "}
                                    {loggedInClinician.lastname}
                                </p>
                                <span className={styles.credentialBadges}>
                                    <Badge
                                        tone="neutral"
                                        content={loggedInClinician.credential}
                                    />
                                    <CredentialExpirationStatus
                                        clinician={loggedInClinician}
                                        experienceLevelOptions={
                                            allExperienceLevelOptions
                                        }
                                    />
                                </span>
                            </div>
                        </div>
                    </div>

                    {headerActions && (
                        <div className={styles.headerActions}>{headerActions}</div>
                    )}
                </div>
                <div className={styles.headerBottom}>
                    <div className={styles.clinicianInfo}>
                        <MetaBox
                            label="Contact Info (Client)"
                            hasError={!clientContact?.trim()}
                            value={
                                isEditingClientContact ? (
                                    <div className={styles.clientInput}>
                                        <Input
                                            value={clientContact}
                                            onChange={(e) =>
                                                setClientContact(e.target.value)
                                            }
                                            hasError={!clientContact?.trim()}
                                        />

                                        <Button
                                            variant="normal"
                                            onClick={handleSave}
                                            className={styles.editActionButton}
                                        >
                                            <SaveCheckIcon stroke="var(--color-primary-v2)" />
                                        </Button>
                                        <Button
                                            variant="normal"
                                            className={styles.editActionButton}
                                            onClick={() =>
                                                setIsEditingClientContact(false)
                                            }
                                        >
                                            <CancelIcon />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className={styles.clientInput}>
                                        <span>
                                            {clientContact}
                                            {!clientContact?.trim() && (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    delay={{ show: 250, hide: 400 }}
                                                    trigger="hover"
                                                    overlay={(props) => (
                                                        <Tooltip
                                                            id="tooltip-client-contact"
                                                            {...props}
                                                        >
                                                            Please enter contact info
                                                            for the client.
                                                        </Tooltip>
                                                    )}
                                                >
                                                    <span>
                                                        <InfoIcon
                                                            width="14"
                                                            height="14"
                                                            stroke="var(--color-text-strong)"
                                                        />
                                                    </span>
                                                </OverlayTrigger>
                                            )}
                                        </span>

                                        <Button
                                            onClick={() =>
                                                setIsEditingClientContact(true)
                                            }
                                            className={styles.editActionButton}
                                            variant="normal"
                                        >
                                            <EditIcon />
                                        </Button>
                                    </div>
                                )
                            }
                            className={styles.clinicianInfoItem}
                        />
                        <MetaBox
                            label="Contact Info (Personal)"
                            value={
                                isEditingPersonalContact ? (
                                    <div className={styles.clientInput}>
                                        <Input
                                            value={personalContact}
                                            onChange={(e) =>
                                                setPersonalContact(e.target.value)
                                            }
                                        />
                                        <Button
                                            variant="normal"
                                            onClick={handleSave}
                                            className={styles.editActionButton}
                                        >
                                            <SaveCheckIcon stroke="var(--color-primary-v2)" />
                                        </Button>
                                        <Button
                                            variant="normal"
                                            className={styles.editActionButton}
                                            onClick={() =>
                                                setIsEditingPersonalContact(false)
                                            }
                                        >
                                            <CancelIcon />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className={styles.clientInput}>
                                        {personalContact || "--"}{" "}
                                        <Button
                                            onClick={() =>
                                                setIsEditingPersonalContact(true)
                                            }
                                            className={styles.editActionButton}
                                            variant="normal"
                                        >
                                            <EditIcon />
                                        </Button>
                                    </div>
                                )
                            }
                            className={styles.clinicianInfoItem}
                        />
                        <MetaBox
                            label="Ideal Caseload"
                            value={
                                isEditingIdealCaseload ? (
                                    <div className={styles.clientInput}>
                                        <Input
                                            value={idealCaseload}
                                            onChange={(e) =>
                                                setIdealCaseload(e.target.value)
                                            }
                                        />
                                        <Button
                                            onClick={handleSave}
                                            variant="normal"
                                            className={styles.editActionButton}
                                        >
                                            <SaveCheckIcon stroke="var(--color-primary-v2)" />
                                        </Button>
                                        <Button
                                            variant="normal"
                                            className={styles.editActionButton}
                                            onClick={() =>
                                                setIsEditingIdealCaseload(false)
                                            }
                                        >
                                            <CancelIcon />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className={styles.clientInput}>
                                        {idealCaseload || "--"}{" "}
                                        <Button
                                            onClick={() =>
                                                setIsEditingIdealCaseload(true)
                                            }
                                            className={styles.editActionButton}
                                            variant="normal"
                                        >
                                            <EditIcon />
                                        </Button>
                                    </div>
                                )
                            }
                            className={styles.clinicianInfoItem}
                        />
                        <MetaBox
                            label="Locations"
                            value={
                                <div className={styles.locations}>
                                    {uniqueLocations.map((l) => (
                                        <Badge tone="info" content={l} />
                                    )) || "No locations"}
                                </div>
                            }
                            className={styles.clinicianInfoItem}
                        />
                    </div>

                    <div>
                        <Button onClick={() => setShowExpLevels(true)}>
                            Update experience levels
                        </Button>
                    </div>
                </div>
            </div>
            <Tabs
                items={tabItems}
                className={styles.tabs}
                defaultActiveKey={activeTab}
            />

            <AddAvailabilityDialog
                open={showAddAvailabilityDialog}
                onClose={() => setShowAddAvailabilityDialog(false)}
                loggedInClinician={loggedInClinician}
                availabilitySchedules={availabilitySchedules}
                heldBookingSchedules={heldBookingSchedules}
                confirmedBookingSchedules={confirmedBookingSchedules}
                officeWindowSchedules={officeWindowSchedules}
                clinicianServiceSelection={clinicianServiceSelection}
                requestedServices={olc?.requestedServices || {}}
                onAvailabilityAdded={() => {
                    if (loggedInUserEmail) {
                        getClinician(loggedInUserEmail);
                    }
                }}
                draftAvailability={draftAvailability}
                setDraftAvailability={setDraftAvailability}
                onSave={handleAddAvailability}
            />

            <ReserveTimeWindowDialog
                open={showReserveTimeWindowDialog}
                onClose={() => setShowReserveTimeWindowDialog(false)}
                loggedInClinician={loggedInClinician}
                availabilitySchedules={availabilitySchedules}
                heldBookingSchedules={heldBookingSchedules}
                confirmedBookingSchedules={confirmedBookingSchedules}
                officeWindowSchedules={officeWindowSchedules}
                clinicianServiceSelection={clinicianServiceSelection}
                requestedServices={olc?.requestedServices || {}}
                onTimeWindowReserved={() => {
                    if (loggedInUserEmail) {
                        getClinician(loggedInUserEmail);
                    }
                }}
                draftReservedWindow={draftReservedWindow}
                setDraftReservedWindow={setDraftReservedWindow}
                onSave={handleReserveTimeWindow}
            />

            <TimeOffDialog
                open={showTimeOffDialog}
                onClose={() => setShowTimeOffDialog(false)}
                draftVacation={draftVacation}
                setDraftVacation={setDraftVacation}
                onSave={handleTimeOff}
            />

            <AddIntakeSessionDialog
                open={showAddIntakeSessionDialog || showEditAvailableIntakeDialog}
                onClose={() => {
                    setShowAddIntakeSessionDialog(false);
                    setShowEditAvailableIntakeDialog(false);
                    setSelectedIntakeSchedule(null);
                }}
                loggedInClinician={loggedInClinician}
                onIntakeSessionAdded={() => {
                    if (loggedInUserEmail) {
                        getClinician(loggedInUserEmail);
                    }
                }}
                draftIntakeSession={draftIntakeSession}
                setDraftIntakeSession={setDraftIntakeSession}
                practiceOptions={intakeSessionPracticeOptions}
                onSave={async () => {
                    if (showEditAvailableIntakeDialog && selectedIntakeSchedule) {
                        await handleEditIntakeSession();
                    } else {
                        await handleAddIntakeSession();
                    }
                }}
                isEdit={showEditAvailableIntakeDialog}
                editTitle={
                    showEditAvailableIntakeDialog ? "Edit Intake Session" : undefined
                }
            />

            <RescheduleIntakeSessionDialog
                open={showEditIntakeSessionDialog}
                onClose={() => {
                    setShowEditIntakeSessionDialog(false);
                    setSelectedIntakeSchedule(null);
                    setIntakeActionReason("");
                }}
                loggedInClinician={loggedInClinician}
                selectedSchedule={selectedIntakeSchedule}
                draftIntakeSession={draftIntakeSession}
                setDraftIntakeSession={setDraftIntakeSession}
                reason={intakeActionReason}
                setReason={setIntakeActionReason}
                onSave={() => {
                    if (selectedIntakeSchedule) {
                        const newIntakeSession: IIntakeAvailability = {
                            id: selectedIntakeSchedule.id as string,
                            start_time: draftIntakeSession.from.toISOString(),
                            end_time: draftIntakeSession.to.toISOString(),
                            recurrence: RecurrenceRule.ONE_OFF,
                            service_delivery_option:
                                draftIntakeSession.serviceDeliveryOption,
                            location: "",
                            office: "",
                            status: "available",
                            clinician_display_name: `${loggedInClinician?.firstname} ${loggedInClinician?.lastname}`,
                            supervisees_display_name:
                                (selectedIntakeSchedule as any)
                                    .supervisees_display_name || "",
                            supervisor_id: loggedInClinician?.id || "",
                            practice_id:
                                draftIntakeSession.practice_id ||
                                (selectedIntakeSchedule as any).practice_id ||
                                "",
                            created_at:
                                (selectedIntakeSchedule as any).created_at ||
                                new Date(),
                        };
                        handleRescheduleIntakeSession(
                            selectedIntakeSchedule,
                            newIntakeSession
                        );
                    }
                }}
                isBooked={true}
            />
            <Dialog
                open={showClientContactPromptDialog}
                onClose={() => {
                    setShowClientContactPromptDialog(false);
                }}
                title="Action required"
                showFooter={false}
            >
                <p>
                    Please provide contact info for clients by adding it in the{" "}
                    <b>Contact Info (Client)</b> field. You can close this message
                    and update that field when you are ready.
                </p>
            </Dialog>

            <Dialog
                open={showCancelIntakeDialog}
                onClose={() => {
                    setShowCancelIntakeDialog(false);
                    setSelectedIntakeSchedule(null);
                    setIntakeActionReason("");
                }}
                isSubmitBtnDisabled={!intakeActionReason.trim()}
                title="Cancel Session"
                saveText="Cancel Session"
                onSave={() => {
                    if (selectedIntakeSchedule) {
                        handleCancelIntakeSession(selectedIntakeSchedule);
                    }
                }}
                cancelText="Close"
            >
                <p>Are you sure you want to cancel this session?</p>
                <div style={{ marginTop: 16 }}>
                    <TextArea
                        label="Reason for cancellation"
                        value={intakeActionReason}
                        onChange={(e) => setIntakeActionReason(e.target.value)}
                        placeholder="Enter reason for cancellation"
                        required
                    />
                </div>
            </Dialog>
        </div>
    );
}
